import {sequenceClips} from './sequence.js';

export const TRANSLATION_LANGUAGES=[
 ['ar','Arabic'],['zh-CN','Chinese (Simplified)'],['zh-TW','Chinese (Traditional)'],
 ['nl','Dutch'],['fr','French'],['de','German'],['hi','Hindi'],
 ['id','Indonesian'],['it','Italian'],['ja','Japanese'],['ko','Korean'],
 ['ms','Malay'],['pt-BR','Portuguese (Brazil)'],['es','Spanish'],
 ['th','Thai'],['uk','Ukrainian'],['vi','Vietnamese']
];
// Selective translation changes only the subtitle export scope, never the
// underlying Assembly or its source interview data.
export function translationProjectForScope(project,scope,selectedPassageId){
 if(scope!=='passage'||!selectedPassageId)return project;
 const paper=project.paperExport;
 if(!paper||!Array.isArray(paper.blocks))return project;
 const passage=paper.blocks.find(block=>block.id===selectedPassageId);
 if(!passage)return project;
 return {...project,paperExport:{...paper,blocks:[passage]}};
}
export const MAX_TRANSLATION_CUES=192;
export const TRANSLATION_BATCH_SIZE=24;
const validWord=word=>Number.isFinite(word?.start)&&Number.isFinite(word?.end)
 &&word.end>word.start&&typeof word.text==='string'&&Boolean(word.text.trim());
const timecode=seconds=>{
 const ms=Math.max(0,Math.round(seconds*1000));
 return [Math.floor(ms/3600000),Math.floor(ms/60000)%60,Math.floor(ms/1000)%60]
  .map(x=>String(x).padStart(2,'0')).join(':')+','+String(ms%1000).padStart(3,'0');
};
// The source edit is authoritative: subtitles NEVER change word boundaries,
// original words, clip order, in/out points or timeline metadata.
export function translationCues(project){
 const paper=project.paperExport;
 const intervals=paper?paper.blocks:sequenceClips(project);
 if(!Array.isArray(intervals)||!intervals.length)throw new Error('Add dialogue to your edit before translating.');
 const cues=[];
 let offset=0;
 for(const part of intervals){
  const source=paper?paper.sources?.[part.sourceId]:project;
  if(!source||!Array.isArray(source.words))
   throw new Error('A source interview has no transcript. Open it and transcribe first.');
  if(!Number.isFinite(part.start)||!Number.isFinite(part.end)||part.end<=part.start)
   throw new Error('The edit contains an invalid clip range.');
  const words=source.words.filter(word=>validWord(word)&&word.start<part.end&&word.end>part.start)
   .sort((a,b)=>a.start-b.start);
  let group=[];
  const flush=()=>{
   if(!group.length)return;
   const first=group[0],last=group.at(-1);
   const start=offset+Math.max(first.start,part.start)-part.start;
   const end=offset+Math.min(last.end,part.end)-part.start;
   const text=group.map(w=>w.text.trim()).join(' ').replace(/\s+/g,' ').trim();
   if(end>start&&text)cues.push({id:`cue-${cues.length+1}`,start,end,text});
   group=[];
  };
  for(const word of words){
   const draft=group.map(w=>w.text).join(' ');
   const differentLine=word.line!=null&&group.length&&group[0].line!=null&&word.line!==group[0].line;
   if(group.length&&(group.length>=13||draft.length+word.text.length>165||
     word.end-group[0].start>6.3||(differentLine&&group.length>=5)))flush();
   group.push(word);
  }
  flush();
  offset+=part.end-part.start;
  if(cues.length>MAX_TRANSLATION_CUES)
   throw new Error('This edit is too long for one AI translation. Use a shorter Assembly or interview selection (up to about 20 minutes of dialogue).');
 }
 if(!cues.length)throw new Error('No spoken transcript words were found in the selected edit.');
 return cues;
}
export const cueBatches=(cues,{batchSize=TRANSLATION_BATCH_SIZE}={})=>{
 const batches=[];let batch=[],size=0;
 for(const cue of cues){
  if(batch.length&&(batch.length>=batchSize||size+cue.text.length>5000)){
   batches.push(batch);batch=[];size=0;
  }
  batch.push({id:cue.id,text:cue.text});size+=cue.text.length;
 }
 if(batch.length)batches.push(batch);
 return batches;
};
export function makeTranslatedSrt(cues,translations){
 if(!cues.length||!translations||cues.some(c=>!translations[c.id]?.trim()))
  throw new Error('Translate every subtitle line before exporting.');
 return cues.map((cue,index)=>`${index+1}\n${timecode(cue.start)} --> ${timecode(cue.end)}\n${translations[cue.id].trim()}`)
  .join('\n\n')+'\n';
}
export function makeTranslatedText(cues,translations){
 if(!cues.length||!translations||cues.some(c=>!translations[c.id]?.trim()))
  throw new Error('Translate every line before exporting.');
 return cues.map(cue=>translations[cue.id].trim()).join('\n\n')+'\n';
}
export function makeBilingualSrt(cues,translations){
 if(!cues.length||!translations||cues.some(c=>!translations[c.id]?.trim()))
  throw new Error('Translate every line before exporting.');
 return cues.map((cue,index)=>`${index+1}\n${timecode(cue.start)} --> ${timecode(cue.end)}\n${cue.text}\n${translations[cue.id].trim()}`).join('\n\n')+'\n';
}
